/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

import * as Blockly from "blockly/core";
import "blockly/python";
import { AreaService } from "@/services/area.service";
import { getAllowedStatements } from "../block_compatibility_checks";
import { areaChange, getYearsArrayFromDates } from '../helper_functions';
import { dataset } from '../dataset'

function generateAnnualLossYears() {
    let fromDate = dataset.getFrom('hansen')
    let toDate = dataset.getTo('hansen')
    let yearsArray = getYearsArrayFromDates(fromDate, toDate);

    let annualLossYears = []
    yearsArray.forEach((year) => {
        if (year !== '2000'){
            annualLossYears.push([year, year])
        }
    });

    return annualLossYears
}

/*
 * Block definition for forest change data selection
 */
Blockly.Blocks['input_hansen_forest_change'] = {
    init: function () {
        var area_dropdown = AreaService.getAreas();
        this.appendDummyInput("heading")
            .appendField(new Blockly.FieldLabelSerializable("Hansen Global Forest Change"), "heading_text")
        this.appendDummyInput("first_input")
            .appendField(new Blockly.FieldLabelSerializable("Parameter: "), "param_text")
            .appendField(
                new Blockly.FieldDropdown([
                    ["Annual Forest Loss", "hansen"],
                    ["Estimated Forest Cover", "hansen_cover"],
                    ["Forest Cover (2000)", "hansen_fnf"],
                    ["Total Loss (2001-2020)", "hansen_loss"],
                    ["Total Gain (2001-2012)", "hansen_gain"],
                    // ["Total Loss and Gain", "hansen_loss_gain"],
                    // ["Total Cover, Loss, and Gain", "hansen_all"]
                ], this.validateParamSelection), "dropdown")
        this.appendDummyInput("inp")
            .appendField(new Blockly.FieldLabelSerializable("Area: "), "area_text")
            .appendField(new Blockly.FieldDropdown(area_dropdown), "area");

        this.appendStatementInput("do").setCheck(
            getAllowedStatements("hansen_time_series")
        );

        this.setInputsInline(false);
        this.setStyle("forest");
        this.setTooltip(Blockly.Msg.HANSEN_DATA_BLOCK_TOOLTIP);
        this.setHelpUrl("");

        this.areaSubscription = AreaService.areaChange$.subscribe( () => {
            let result = areaChange(this, "inp", "area");
            if(!result) {
                this.areaSubscription.unsubscribe();
            }
        });
    },

    /**
     * On user selection change of data type, check if the value is equal to hansen aka annual loss
     * and if so set the showCanopyCover boolean and call updateCanopyCoverField
     * @param {String} newValue the users selected value
     * @returns {String} validateValue - returns the validated value
     */
    validateParamSelection: function(newValue) {
        let sourceBlock = this.getSourceBlock();
        sourceBlock.showCanopyCover = newValue === 'hansen' || newValue === 'hansen_cover';
        sourceBlock.showTimeseriesCheckbox = newValue === 'hansen' || newValue === 'hansen_cover';
        sourceBlock.showDateSelection = newValue === 'hansen';
        sourceBlock.showLimitedDateSelection = newValue === 'hansen_cover';
        sourceBlock.updateDateSelection();
        sourceBlock.updateCanopyCoverField();
        sourceBlock.updateTimeseriesCheckbox();
        return newValue;
    },

    /**
     * Called whenever the user selects a new dropdown option, checks boolean showDateSelection to see
     * if the year selection dropdown should be appended or removed
     */
    updateDateSelection: function () {
        
        let generatedAnnualLossYears = generateAnnualLossYears();
        // removal of fields, this must be done first as per TRIAL-672 as there will be two fields
        // with the ID 'td_yearly' so a getField() call has unintended side effects
        // remove limited date selection fields
        if (!this.showLimitedDateSelection && this.getInput("limited_date_selection")) {
            this.removeInput("limited_date_selection")
        }
        // remove date selection fields
        if (!this.showDateSelection && this.getInput("date_selection")) {
            this.removeInput("date_selection")
        }

        if(this.showDateSelection && !this.getInput("date_selection")) {
            this.appendDummyInput("date_selection")
                .setAlign(Blockly.ALIGN_LEFT);

            // move input before statement input
            this.moveInputBefore("date_selection", "do");
            // if canopy cover controls already exist, move the date selection before
            if(this.getInput("canopyCoverControls")) this.moveInputBefore("date_selection", "canopyCoverControls")

            let dateSelection = this.getInput("date_selection");

            dateSelection
                .appendField("From:", "from_text")
                .appendField(new Blockly.FieldDropdown(generatedAnnualLossYears), "fd_yearly")
                .appendField("To:", "to_text")
                .appendField(new Blockly.FieldDropdown(generatedAnnualLossYears), "td_yearly")

            // update final dropdown to be at the end date of the dataset            
            this.getField("td_yearly").setValue("2021");
        }

        // limited date selection has to be done as a new field rather than updating the old one as it
        // has a new option the year 2000
        if(this.showLimitedDateSelection && !this.getInput("limited_date_selection")) {
            this.appendDummyInput("limited_date_selection")
                .setAlign(Blockly.ALIGN_LEFT);

            // move input before statement input
            this.moveInputBefore("limited_date_selection", "do");
            // if canopy cover controls already exist, move the date selection before
            if(this.getInput("canopyCoverControls")) this.moveInputBefore("limited_date_selection", "canopyCoverControls")

            let dateSelection = this.getInput("limited_date_selection");

            dateSelection
                .appendField("From:", "from_text")
                .appendField(new Blockly.FieldDropdown([['2000', '2000']]), "fd_yearly")
                .appendField("To:", "to_text")
                .appendField(new Blockly.FieldDropdown(generatedAnnualLossYears), "td_yearly")

            // update final dropdown to be at the end date of the dataset            
            this.getField("td_yearly").setValue("2021");
        }
    },

    /**
     * called whenever the user selects a dropdown option, checks boolean showCanopyCover to see if the
     * canopyCoverControl fields need to be added/removed from the block
     */
    updateCanopyCoverField: function() {
        if(this.showCanopyCover && !this.getInput("canopyCoverControls")) {
            this.appendDummyInput("canopyCoverControls")
                .setAlign(Blockly.ALIGN_LEFT);

            // move new dummy input to before the C block section
            this.moveInputBefore("canopyCoverControls", "do")

            // create number field
            let canopyCoverControls = this.getInput("canopyCoverControls");
            canopyCoverControls
                .appendField("Where canopy cover is at least")
                .appendField(
                    new Blockly.FieldNumber(
                        10, // default
                        0, // min
                        100, // max
                        1 // precision
                    ), "minimumCanopyCover")
                .appendField("%")

        } else if (!this.showCanopyCover && this.getInput("canopyCoverControls")){
            // remove input
            this.removeInput("canopyCoverControls");
        }
    },

    /**
     * called whenever the user selects a dropdown option, checks boolean showTimeseriesCheckbox to see if the
     * timeseriesControls fields need to be added/removed from the block
     */
    updateTimeseriesCheckbox: function() {
        if(this.showTimeseriesCheckbox && !this.getInput("timeseriesControls")) {
            this.appendDummyInput("timeseriesControls")
                .setAlign(Blockly.ALIGN_LEFT);

            // move new dummy input to before the C block section
            this.moveInputBefore("timeseriesControls", "do");

            let timeseriesControls = this.getInput("timeseriesControls");
            timeseriesControls
                .appendField("Show me a timeseries animation")
                .appendField(new Blockly.FieldCheckbox(false), 'isTimeseries')
        } else if (!this.showTimeseriesCheckbox && this.getInput("timeseriesControls")) {
            // remove input
            this.removeInput("timeseriesControls")
        }
    }
};